<template>
    <!-- COMPACT -->
  <div v-if="compactOneLine && !overrideCompactOneLine">
    <div class="definition-result-container">
      <div class="row results-row compactOneLine">
        <div class="col-sm-4">
          <div class="tibetan-text text" @click="overrideCompactOneLine=true"><span v-html="this.tibetan"></span></div>
        </div>
        <div class="col-sm-8">
          <div class="compact-definition-container">
            <div v-if="result.notFound">Exact Match Not Found</div>
            <SmallResult :data="getSmallResult()" />
          </div>
        </div>
      </div>
    </div>
  </div>
  <!-- SMALL -->
  <div v-else-if="small">
    <div class="definition-result-container">
      <font-awesome-icon class="icon-float copy-icon" icon="copy" @click="copyToClipboard"/>
      <div class="row results-row">
        <div class="col-sm-5">
          <div v-if="shouldAllowSubsearch()" class="tibetan-text text">
            <span target="_blank" v-html="this.tibetan"></span>
            <a target="_blank" href="#" @click.prevent="doTranslateTool"><font-awesome-icon class="icon-search" icon="search" /></a>
          </div>
          <div v-else class="tibetan-text text"><a target="_blank" v-html="this.tibetan"></a></div>
        </div>
        <div class="col-sm-7">
          <div class="right-side">
            <div v-if="result.notFound">Exact Match Not Found</div>
            <div v-else>{{getSmallResult().definition}}</div>
          </div>
        </div>
      </div>
    </div>
  </div>
  <!-- LOADING -->
  <div v-else-if="this.result.loading">
    <div class="definition-result-container" :id="'result-loader'">
      <div class="row results-row">
          <div class="spinner-border subsearch-loading" role="status">
            <span class="sr-only">Loading...</span>
          </div>
      </div>
    </div>
  </div>
  <!-- MAIN RESULT CONTAINER -->
  <div v-else>
    <div class="definition-result-container" :class="{ 'fade-out': isHiding }" :id="'result-' + index">
    <div class="top-right-icon-container">
      <font-awesome-icon class="icon-float link-icon" icon="link" @click="copyLinkToClipboard" v-tooltip="`Copy direct link for this entry to clipboard`"/>
      <font-awesome-icon class="icon-float copy-icon" icon="copy" @click="copyToClipboard" v-tooltip="`Copy term and definition to clipboard`"/>
      <a class="icon-float monlam-icon" style="font-weight: bold;" target="_blank" :href="this.generateMonlamLink(this.result.tibetan)" v-tooltip="`Look up term in Grand Monlam Dictionary`">M</a>
      <a class="icon-float monlam-icon" style="font-weight: bold;" target="_blank" :href="this.generateSteinertLink(this.result.tibetan)" v-tooltip="`Look up term in Steinert's Dictionary`">S</a>
    </div>
      <!-- RESULT ROW -->
      <div class="row results-row">
        <!-- DEFINIENDUM -->
        <div class="col-sm-4 definiendum-container">
          <!-- SUBSEARCH ICON -->
          <div v-if="shouldAllowSubsearch()" class="tibetan-text text">
            <span target="_blank" v-html="this.tibetan" @click="if (compactOneLine && overrideCompactOneLine) overrideCompactOneLine=false"></span>
            <a target="_blank" href="#" @click.prevent="doTranslateTool" v-tooltip="'Click to break this term down into smaller terms'"><font-awesome-icon class="icon-search" icon="search" tooltip="Perform a subsearch"/></a>
          </div>
          <div v-else class="tibetan-text text" @click="if (compactOneLine && overrideCompactOneLine) overrideCompactOneLine=false"><span target="_blank" v-html="this.tibetan"></span></div>
          <div class="text" v-if="this.$store.state.app.showWylieInDefiniendum" v-html="this.definiendum"></div>
          <div v-if="this.sanskrit && this.$store.state.app.showSanskritInDefiniendum" class="text" v-html="this.sanskrit"></div>
          <div style="font-size:12px; opacity:0.5" v-if="this.showScore">Sort score: {{Number(result.score).toPrecision(3)}}</div>
          <font-awesome-icon v-if="isTranslationToolMainSearch" class="link-icon icn-hide-term" icon="eye-slash" @click="hideTermFromTranslationTool" v-tooltip="'Hide this term from translation tool results (can be undone from Settings)'"/>
        </div>
        <!-- DEFINITONS -->
        <div class="col-sm-8">
          <div class="right-side">
            <!-- NOT FOUND -->
            <div v-if="result.notFound">Exact Match Not Found</div>

            <!-- SHOW FILTERED RESULTS BUTTON -->
            <div v-if="hasOnlyFilteredContent() && !showFilteredContent" class="filtered-content-notice" >
              <button @click="handleShowFilteredClick" class="btn-show-filtered" v-tooltip="'No results found for selected dictionaries. Click to show results from all dictionaries.'">
                Show Filtered Results
              </button>
            </div>

            <!-- UMA TIBET / HOPKINS -->
            <div v-if="hasData('umaTibet', result) && isDictionaryEnabled('umaTibet')">
              <div class="dict-marker">
                <span @click="toggleSection('umaTibet')" class="dict-header-clickable">
                  <font-awesome-icon :icon="isSectionCollapsed('umaTibet') ? 'chevron-right' : 'chevron-down'" class="chevron-icon"/>
                  UMA Tibet Dictionary
                </span>
              </div>
              <transition name="collapse">
                <div class="box" v-show="!isSectionCollapsed('umaTibet')">
                <Result v-if="result.hopkins_2015" title="" :data="result.hopkins_2015" columnName="hopkins_2015" :entryId="result.id" />
                <Result v-else title="" :data="result.english1" columnName="english1" :entryId="result.id" />
                <Result title="English2" :data="result.english2" columnName="english2" :entryId="result.id"/>
                <Result title="Other" :data="result.english_others" columnName="english_others" :entryId="result.id" />
                <Result title="Hackett" :data="result.hackett_definitions" columnName="hackett_definitions" :entryId="result.id" />

                <Result title="Definition" :data="result.definition_tibetan_unicode" :largerFont="true" columnName="definition_tibetan_unicode" :entryId="result.id"/>
                <Result title="Definition" :data="result.definition_english" columnName="definition_english" :entryId="result.id" />

                <Result title="Divisions" :data="result.divisions_tibetan_unicode" :largerFont="true" columnName="divisions_tibetan_unicode" :entryId="result.id" />
                <Result title="Divisions" :data="result.divisions_english" columnName="divisions_english" :entryId="result.id" />

                <Result title="Synonyms" :data="result.synonyms_tibetan_unicode" :largerFont="true" columnName="synonyms_tibetan_unicode" :entryId="result.id" />
                <Result title="Synonyms" :data="result.synonyms_english" columnName="synonyms_english" :entryId="result.id" />

                <TenseResult v-if="result.tenses_unicode" title="Tenses" :data="result.tenses_unicode" :largerFont="true" columnName="tenses_unicode" :entryId="result.id" />
                <TenseResult v-else title="Tenses" :data="result.tenses" columnName="tenses" :entryId="result.id" />

                <Result title="Example1" :data="result.example_tibetan1_unicode" :largerFont="true" columnName="example_tibetan1_unicode" :entryId="result.id" />
                <Result title="Example1" :data="result.example_english1" columnName="example_english1" :entryId="result.id" />

                <Result title="Example2" :data="result.example_tibetan2_unicode" :largerFont="true" columnName="example_tibetan2_unicode" :entryId="result.id" />
                <Result title="Example2" :data="result.example_english2" columnName="example_english2" :entryId="result.id" />

                <Result title="Example3" :data="result.example_tibetan3_unicode" :largerFont="true" columnName="example_tibetan3_unicode" :entryId="result.id" />
                <Result title="Example3" :data="result.example_english3" columnName="example_english3" :entryId="result.id" />

                <Result title="Example4" :data="result.example_tibetan4_unicode" :largerFont="true" columnName="example_tibetan4_unicode" :entryId="result.id" />
                <Result title="Example4" :data="result.example_english4" columnName="example_english4" :entryId="result.id"/>

                <Result title="Example5" :data="result.example_tibetan5_unicode" :largerFont="true" columnName="example_tibetan5_unicode" :entryId="result.id" />
                <Result title="Example5" :data="result.example_english5" columnName="example_english5" :entryId="result.id"/>

                <Result title="Example6" :data="result.example_tibetan6_unicode" :largerFont="true" columnName="example_tibetan6_unicode" :entryId="result.id" />
                <Result title="Example6" :data="result.example_english6" columnName="example_english6" :entryId="result.id" />

                <Result title="Comments" :data="result.comments" columnName="comments" :entryId="result.id" />
                </div>
              </transition>
            </div>

            <!-- YELLOW HACKETT INDEX -->
            <div v-if="hasData('hackettIndex', result) && isDictionaryEnabled('hackettIndex')">
              <div class="dict-marker">
                <span @click="toggleSection('hackettIndex')" class="dict-header-clickable">
                  <font-awesome-icon :icon="isSectionCollapsed('hackettIndex') ? 'chevron-right' : 'chevron-down'" class="chevron-icon"/>
                  Yellow Hackett Index
                </span>
              </div>
              <transition name="collapse">
                <HackettIndexResult v-show="!isSectionCollapsed('hackettIndex')" title="" :data="JSON.parse(this.result.hackett_index)" columnName="hacket_index" :entryId="result.id" />
              </transition>
            </div>

            <!-- VERBINATOR -->
            <div v-if="hasData('verbinator', result) && isDictionaryEnabled('verbinator')">
              <div class="dict-marker">
                <span @click="toggleSection('verbinator')" class="dict-header-clickable">
                  <font-awesome-icon :icon="isSectionCollapsed('verbinator') ? 'chevron-right' : 'chevron-down'" class="chevron-icon"/>
                  Nathan Hill's Verbinator
                </span>
              </div>
              <transition name="collapse">
                <VerbinatorResult v-show="!isSectionCollapsed('verbinator')" title="Verbinator" :data="result.verbinator" columnName="verbinator" :entryId="result.id" />
              </transition>
            </div>

            <!-- RANGJUNG YESHE -->
            <div v-if="hasData('rangjung', result) && isDictionaryEnabled('rangjung')">
              <div class="dict-marker">
                <span @click="toggleSection('rangjung')" class="dict-header-clickable">
                  <font-awesome-icon :icon="isSectionCollapsed('rangjung') ? 'chevron-right' : 'chevron-down'" class="chevron-icon"/>
                  Rangjung Yeshe
                </span>
              </div>
              <transition name="collapse">
                <div class="box" v-show="!isSectionCollapsed('rangjung')">
                  <Result title="" :data="result.rangjung_english" process-wylie="true" process-numbers="true" columnName="rangjung_english" :entryId="result.id" />
                </div>
              </transition>
            </div>

            <!-- BERZIN -->
            <div v-if="hasData('berzin', result) && isDictionaryEnabled('berzin')">
              <div class="dict-marker">
                <span @click="toggleSection('berzin')" class="dict-header-clickable">
                  <font-awesome-icon :icon="isSectionCollapsed('berzin') ? 'chevron-right' : 'chevron-down'" class="chevron-icon"/>
                  Berzin
                </span>
              </div>
              <transition name="collapse">
                <div class="box" v-show="!isSectionCollapsed('berzin')">
                  <Result title="" :data="result.berzin" />
                  <Result title="Berzin Definitions" :data="result.berzin_definitions" column-name="berzin_definitions" :entryId="result.id" />
                </div>
              </transition>
            </div>

            <!-- 84000 -->
            <div v-if="hasData('eightFourThousand', result) && isDictionaryEnabled('eightFourThousand')">
              <div class="dict-marker">
                <span @click="toggleSection('eightFourThousand')" class="dict-header-clickable">
                  <font-awesome-icon :icon="isSectionCollapsed('eightFourThousand') ? 'chevron-right' : 'chevron-down'" class="chevron-icon"/>
                  84000
                </span>
              </div>
              <transition name="collapse">
                <div class="box" v-show="!isSectionCollapsed('eightFourThousand')">
                  <Result title="" :data="result.eightfourthousand_dict" column-name="eightfourthousand_dict" :entryId="result.id" />
                  <Result title="Definitions" :data="result.eightfourthousand_def" column-name="eightfourthousand_def" :entryId="result.id" />
                  <Result title="Sanskrit" :data="result.eightfourthousand_skt" column-name="eightfourthousand_skt" :entryId="result.id" />
                  <Result title="Synonyms" :data="result.eightfourthousand_syn" column-name="eightfourthousand_syn" :entryId="result.id" />
                </div>
              </transition>
            </div>

            <!-- TSEPAK RIGZIN -->
            <div v-if="hasData('tsepakRigzin', result) && isDictionaryEnabled('tsepakRigdzin')">
              <div class="dict-marker">
                <span @click="toggleSection('tsepakRigdzin')" class="dict-header-clickable">
                  <font-awesome-icon :icon="isSectionCollapsed('tsepakRigdzin') ? 'chevron-right' : 'chevron-down'" class="chevron-icon"/>
                  Tsepak Rigdzin
                </span>
              </div>
              <transition name="collapse">
                <div class="box" v-show="!isSectionCollapsed('tsepakRigdzin')">
                  <Result title="" :data="result.tsepak_rigdzin" column-name="tsepak_rigdzin" :entryId="result.id" />
                </div>
              </transition>
            </div>

            <!-- tshig mdzod chen mo -->
            <div v-if="hasData('tshigMdzod', result) && isDictionaryEnabled('tshigMdzod')">
              <div class="dict-marker" style="font-size: 160%">
                <span @click="toggleSection('tshigMdzod')" class="dict-header-clickable">
                  <font-awesome-icon :icon="isSectionCollapsed('tshigMdzod') ? 'chevron-right' : 'chevron-down'" class="chevron-icon"/>
                  ཚིག་མཛོད་ཆེན་མོ།
                </span>
              </div>
              <transition name="collapse">
                <div class="box" v-show="!isSectionCollapsed('tshigMdzod')">
                  <Result title="" noColon="true" :data="result.tshig_mdzod_chen_mo" style="font-size: 160%" column-name="tshig_mdzod_chen_mo" :entryId="result.id" allowsTranslation/>
                </div>
              </transition>
            </div>

            <!-- dung dkar tshig mdzod chen mo -->
            <div v-if="hasData('dungDkar', result) && isDictionaryEnabled('dungDkar')">
              <div class="dict-marker" style="font-size: 160%">
                <span @click="toggleSection('dungDkar')" class="dict-header-clickable">
                  <font-awesome-icon :icon="isSectionCollapsed('dungDkar') ? 'chevron-right' : 'chevron-down'" class="chevron-icon"/>
                  དུང་དཀར་ཚིག་མཛོད་ཆེན་མོ།
                </span>
              </div>
              <transition name="collapse">
                <div class="box" v-show="!isSectionCollapsed('dungDkar')">
                  <Result title="" noColon="true" :data="result.dung_dkar_tshig_mdzod_chen_mo" style="font-size: 160%" column-name="dung_dkar_tshig_mdzod_chen_mo" :entryId="result.id" allowsTranslation />
                </div>
              </transition>
            </div>

            <!-- dag tshig -->
            <div v-if="hasData('dagTshig', result) && isDictionaryEnabled('dagTshig')">
              <div class="dict-marker" style="font-size: 160%">
                <span @click="toggleSection('dagTshig')" class="dict-header-clickable">
                  <font-awesome-icon :icon="isSectionCollapsed('dagTshig') ? 'chevron-right' : 'chevron-down'" class="chevron-icon"/>
                  དག་ཚིག་གསར་བསྒྲིགས།
                </span>
              </div>
              <transition name="collapse">
                <div class="box" v-show="!isSectionCollapsed('dagTshig')">
                  <Result title="" noColon="true" :data="result.dag_tshig_gsar_bsgrigs" style="font-size: 160%" column-name="dag_tshig_gsar_bsgrigs" :entryId="result.id" allowsTranslation/>
                </div>
              </transition>
            </div>

            <!-- Steiner Link -->
            <!--<div style="margin-top: 10px" class="text"><a class="steinert-dict" target="_blank" :href="generateSteinertLink(result.definiendum)" v-tooltip="'Click on this link to search for this term in Christian Steinert\'s dictionary'"><font-awesome-icon class="icon-search-steinert" icon="search"/> Steinert's Dictionary</a></div>-->

            <!-- Notes -->
            <NotesResult title="Notes" :notesText="result.notes" :entryId="result.id"/>
          </div>
        </div>
        <!-- END DEFINITIONS -->
      </div>
      <!-- Alternative Matches (expandable, from segmentation tree) -->
      <div v-if="result._treeChildren && result._treeChildren.length > 0" class="alt-matches-section">
        <div class="alt-matches-toggle" @click="showAlternatives = !showAlternatives">
          <font-awesome-icon :icon="showAlternatives ? 'chevron-down' : 'chevron-right'" class="chevron-icon"/>
          <span class="alt-matches-label">
            {{ result._treeChildren.length }} alternative{{ result._treeChildren.length > 1 ? 's' : '' }}
          </span>
        </div>
        <transition name="collapse">
          <div v-show="showAlternatives" class="alt-matches-list">
            <div v-for="(alt, ai) in result._treeChildren" :key="'alt-'+ai" class="alt-match-item">
              <span class="alt-tibetan">{{ alt.tibetan }}</span>
              <span v-if="alt.variant && alt.variant !== 'base'" class="alt-variant">[{{ alt.variant }}]</span>
              <div class="alt-english">{{ getFirstDefinition(alt) }}</div>
            </div>
          </div>
        </transition>
      </div>
    </div>

    <!-- Hide Term Confirmation Dialog -->
    <div v-if="showHideTermDialog" class="hide-term-modal-overlay" @click="cancelHideTerm">
      <div class="hide-term-modal" @click.stop>
        <div class="hide-term-modal-header">
          <h4>Hide Term from Translation Tool</h4>
        </div>
        <div class="hide-term-modal-body">
          <p>This will hide the term <strong>{{ getTermToHide() }}</strong> from all future Translation Tool results by adding it to an ignore list.</p>
          <p>You can undo this later in the Settings page under "Translation Tool Preferences".</p>
        </div>
        <div class="hide-term-modal-footer">
          <button class="btn btn-secondary" @click="cancelHideTerm">Cancel</button>
          <button class="btn btn-primary" @click="confirmHideTerm">Hide Term</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>

  import Result from './Result';
  import VerbinatorResult from "@/components/VerbinatorResult";
  import HackettIndexResult from "@/components/HackettIndexResult";
  import TenseResult from "@/components/TenseResult";
  import NotesResult from "@/components/NotesResult";
  import SmallResult from '@/components/SmallResult.vue';
  import { mapState } from 'vuex'
  import * as Utils from '../utils'
  import dictionaries from './dictionaries';

  export default {
    name: 'DefinitionComponent',
    props: {
      result: Object,
      index: Number,
      translateTool: Function,
      small: Boolean,
      compactOneLine: {
        type: Boolean,
        value: false
      }
    },
    data() {
      return {
        showScore: false,
        showFilteredContent: false,
        isHiding: false,
        showAlternatives: false,
        overrideCompactOneLine: false,
        showHideTermDialog: false
      }
    },
    components: {
      Result, VerbinatorResult, HackettIndexResult, NotesResult, TenseResult, SmallResult
    },
    mounted() {
      // Load preferences when component mounts if not already loaded
      if (Object.keys(this.$store.state.app.collapsedDictionarySections).length === 0) {
        // No need to load - already handled by vuex-persist
      }

      // Auto-show filtered content if setting is enabled and there's only filtered content
      if (this.$store.state.app.autoShowFilteredResults && this.hasOnlyFilteredContent()) {
        this.showFilteredContent = true;
      }
    },
    methods: {
      handleShowFilteredClick() {
        // Show the filtered content
        this.showFilteredContent = true;

        // If user hasn't been informed about the auto-show setting, inform them now
        if (!this.$store.state.app.hasBeenAskedAboutAutoShow) {
          // Use setTimeout to show dialog after the content is displayed
          setTimeout(() => {
            const message = `You can automatically show filtered results instead of having to click this button by selecting "Automatically show filtered results when no visible results found" on the Settings page.`;

            alert(message);

            // Mark that we've informed the user
            this.$store.commit('app/setHasBeenAskedAboutAutoShow', true);
          }, 100);
        }
      },
      toggleSection(sectionName) {
        this.$store.commit('app/toggleDictionarySection', sectionName);
      },
      isSectionCollapsed(sectionName) {
        return this.$store.state.app.collapsedDictionarySections[sectionName] || false;
      },
      isDictionaryEnabled(dictName) {
        // If user clicked "Show Filtered Results" for this entry, show everything
        if (this.showFilteredContent) {
          return true;
        }

        // Check if this is a translation tool search
        const isTranslationTool = this.$store.state.app.searchType === 'translation-tool';
        const filters = this.$store.state.app.dictionaryFilters;

        if (isTranslationTool) {
          return filters.translationTool[dictName] !== false;
        } else {
          return filters.normalSearch[dictName] !== false;
        }
      },
      hasOnlyFilteredContent() {
        // Check if this result has content but all of it is filtered out
        const hasContent = dictionaries.hasContentByArray([
          'umaTibet',
          'hackettIndex',
          'verbinator',
          'rangjung',
          'berzin',
          'eightFourThousand',
          'tshigMdzod',
          'dungDkar',
          'dagTshig',
          'tsepakRigzin'
        ], this.result)

        if (!hasContent) return false; // No content at all

        // Check if any content is visible with current filters

        const hasVisibleContent = dictionaries.hasContentByArray([
          'umaTibet',
          'hackettIndex',
          'verbinator',
          'rangjung',
          'berzin',
          'eightFourThousand',
          'tshigMdzod',
          'dungDkar',
          'dagTshig',
          'tsepakRigzin'
        ], this.result, this.isDictionaryEnabledForCheck.bind(this))

        return hasContent && !hasVisibleContent;
      },
      isDictionaryEnabledForCheck(dictName) {
        // This is just for checking, doesn't consider showFilteredContent override
        const isTranslationTool = this.$store.state.app.searchType === 'translation-tool';
        const filters = this.$store.state.app.dictionaryFilters;

        if (isTranslationTool) {
          return filters.translationTool[dictName] !== false;
        } else {
          return filters.normalSearch[dictName] !== false;
        }
      },
      generateSteinertLink(term) {
        let bracketsRemoved = term.replace(/\[.*\]$/gm, '');
        return 'http://dictionary.christian-steinert.de/#'+encodeURIComponent(`{"activeTerm":"${bracketsRemoved}","currentListTerm":"${bracketsRemoved}","forceLeftSideVisible":false,"offset":0}`)
      },
      generateMonlamLink(term) {
        let bracketsRemoved = term.replace(/\[.*\]$/gm, '');
        return `https://bum-pa-mi-rtag-pa.site/dictionary/#/?searchText=${encodeURIComponent(bracketsRemoved)}&searchType=monlam-tibetanToTibetan&limit=10`;
      },
      getDictionaryNameForFieldName(name) {
        return dictionaries.dictionaryNameForFieldName(name, false) || "";
      },
      getAbbreviatedDictionaryNameForFieldName(name) {
        return dictionaries.dictionaryNameForFieldName(name, true) || "";
      },
      checkFields(fieldObjects, preferredDictionaries = null) {
          console.log('checkFields called with preferredDictionaries:', preferredDictionaries);
          for (let i = 0; i < fieldObjects.length; i++) {
            const fieldObj = fieldObjects[i];
            const field = fieldObj.name;
            const dictionary = fieldObj.dictionary;

            console.log(`Checking field: ${field} (${dictionary})`);

            if (this.result[field] != '' && this.result[field] != null) {
              // If preferredDictionaries is provided, check if this field belongs to a preferred dictionary
              if (preferredDictionaries && preferredDictionaries.length > 0) {
                if (!preferredDictionaries.includes(dictionary)) {
                  console.log(`  Skipping ${field} (${dictionary}) - not in preferred dictionaries`);
                  continue; // Skip this field if it's not in a preferred dictionary
                }
              }
              const found = {
                definition: this.result[field],
                fieldName: field,
                dictionaryName: this.getAbbreviatedDictionaryNameForFieldName(field)
              }
              console.log(`  Found result in ${field}:`, found);
              return found;
            } else {
              console.log(`  Field ${field} is empty or null`);
            }
          }
          console.log('No result found in checkFields');
          return false;
      },
      getSmallResult() {
        const activeDictionaries = this.$store.getters['app/activeDictionaries'];

        // Determine which set of active dictionaries to use based on searchType
        const preferredDictionaries = this.searchType === 'translation-tool'
          ? activeDictionaries.translationTool
          : activeDictionaries.normalSearch;

        let fields = [
          { name: 'english1', dictionary: 'umaTibet' },
          { name: 'rangjung_english', dictionary: 'rangjung' },
          { name: 'eightfourthousand_def', dictionary: 'eightFourThousand' },
          { name: 'berzin_definitions', dictionary: 'berzin' },
          { name: 'tsepak_rigzin', dictionary: 'tsepakRigzin'},
          { name: 'english2', dictionary: 'umaTibet' },
          { name: 'hopkins_2015', dictionary: 'umaTibet' },
          { name: 'english_others', dictionary: 'umaTibet' },
          { name: 'hackett_definitions', dictionary: 'umaTibet' },
          { name: 'dag_tshig_gsar_bsgrigs', dictionary: 'dagTshig' },
          { name: 'tshig_mdzod_chen_mo', dictionary: 'tshigMdzod' },
          { name: 'dung_dkar_tshig_mdzod_chen_mo', dictionary: 'dungDkar' }
        ];

        // First pass: try to find a field from active dictionaries
        let result = this.checkFields(fields, preferredDictionaries);
        if (result) return result;

        console.log("No result for preferred, falling back to all ...")

        // Fallback: try all fields from any dictionary
        result = this.checkFields(fields);
        if (result) return result;

        // No definition found
        return {
          definition: 'NONE FOUND',
          fieldName: '',
          dictionaryName: ''
        };
      },
      hasData(dictionaryKey, result) {
        if (!dictionaries.asObject[dictionaryKey]) {
          throw new Error(`Unknown dictionary key: ${dictionaryKey}`);
        }
        return dictionaries.asObject[dictionaryKey].hasData(result);
      },
      shouldAllowSubsearch() {
        if (this.result.subsearchPerformed) return false;

        // Check if term has at least one tsheg (syllable separator)
        if (!this.result.tibetan.match(/[\u0F0B]+[^\u0F0B]+[\u0F0B]*$/)) return false;

        // Remove brackets and trailing punctuation, then split by tsheg
        const cleaned = this.result.tibetan.replace(/\[.*\]$/gm, '').replace(/[།་]+$/g, '');
        const syllables = cleaned.split('་').filter(s => s.length > 0);

        // If there are only 2 syllables, check if the last one is pa/po/ba/bo
        if (syllables.length === 2) {
          const lastSyllable = syllables[1];
          if (lastSyllable === 'པ' || lastSyllable === 'པོ' ||
              lastSyllable === 'བ' || lastSyllable === 'བོ') {
            return false;
          }
        }

        return true;
      },
      doTranslateTool() {
        // ignore anything bracketed at end in subsearch
        let bracketsRemoved = this.result.tibetan.replace(/\[.*\]$/gm, '');
        this.translateTool(this.index, bracketsRemoved);
      },
      copyLinkToClipboard() {
        const URL = `${window.location.origin}?searchText=${encodeURIComponent(this.result.tibetan)}&searchType=definiendumExact&limit=1`
        let that = this;
        this.$copyText(URL).then(function () {
          that.$log.info("Copied to clipboard: " + URL);
        }, function (e) {
          that.$log.error("Failed to copy to clipboard.");
          that.$log.error(e);
        })
      },
      copyToClipboard() {

        let text = "";

        let bracketsRemoved = this.result.tibetan.replace(/\[.*\]$/gm, '');
        let english = this.result.hopkins_2015 || this.result.english1 || this.result.rangjung_english || this.result.berzin || this.result.eightfourthousand_dict || "";
        text = `${bracketsRemoved} ${english}`;

        let that = this;
        this.$copyText(text).then(function () {
          that.$log.info("Copied to clipboard: " + text);
        }, function (e) {
          that.$log.error("Failed to copy to clipboard.");
          that.$log.error(e);
        })
      },
      getFirstDefinition(item) {
        const def = item.hopkins_2015 || item.english1 || item.rangjung_english || item.berzin || item.eightfourthousand_dict || '';
        if (!def) return '';
        return def.length > 150 ? def.substring(0, 150) + '...' : def;
      },
      hideTermFromTranslationTool() {
        // Check if user has seen the dialog before
        if (!this.$store.state.app.hasSeenHideTermDialog) {
          // Show confirmation dialog
          this.showHideTermDialog = true;
        } else {
          // User has seen the dialog before, proceed directly
          this.proceedWithHidingTerm();
        }
      },
      getTermToHide() {
        // Get the Tibetan term without brackets, trailing tsheg, and shad for display
        return this.result.tibetan
          .replace(/\[.*\]$/gm, '')
          .replace(/[་།]+$/g, '');
      },
      confirmHideTerm() {
        // Mark that user has seen the dialog
        this.$store.commit('app/setHasSeenHideTermDialog', true);

        // Close dialog
        this.showHideTermDialog = false;

        // Proceed with hiding the term
        this.proceedWithHidingTerm();
      },
      cancelHideTerm() {
        // Just close the dialog without doing anything
        this.showHideTermDialog = false;
      },
      proceedWithHidingTerm() {
        // Get the Tibetan term without brackets, trailing tsheg, and shad
        let tibetanTerm = this.result.tibetan
          .replace(/\[.*\]$/gm, '')
          .replace(/[་།]+$/g, '');

        // Add to ignore list
        this.$store.commit('app/addToTranslationToolIgnoreList', tibetanTerm);

        // Set hiding flag for fade-out animation
        this.isHiding = true;

        // Wait for animation to complete, then emit event to parent to remove this item from results
        setTimeout(() => {
          this.$emit('term-hidden', this.index);
        }, 500); // Match the animation duration (0.5s)
      }
    },
    computed: {
      ...mapState('app', [
        'searchText',
        'searchType',
        'collapsedDictionarySections',
        'dictionaryFilters'
      ]),
      ...mapState('user', [
        'userId'
      ]),
      isTranslationToolMainSearch() {
        // Only show hide button for translation tool main search results (not subsearches)
        return this.searchType === 'translation-tool' && this.result.subsearchLevel === 0;
      },
      tibetan() {
        let processed = Utils.processTranslationToolIgnores(this.result.tibetan, "color: #db7575; margin-left:2px;")
        return Utils.highlight(processed, this.searchText, "highlight", this.searchType);
      },
      definiendum() {
        return Utils.highlight(this.result.definiendum, this.searchText, "highlight", this.searchType);
      },
      sanskrit() {
        if (!this.result.sanskrit) return this.result.sanskrit;
        else return Utils.highlight(this.result.sanskrit, this.searchText, "highlight", this.searchType).replace(/{[\w, ]+}/gm, '');
      },
      wylie() {
        return Utils.highlight(this.result.wylie, this.searchText, "highlight", this.searchType);
      }
    }
  }
</script>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style scoped>

  .dict-marker {
    color: #737373;
    font-weight: bold;
    margin-bottom:10px;
    margin-top:10px;
  }

  .copy-to-clipboard-container {
    visibility: hidden;
  }

  .definition-result-container:hover .copy-to-clipboard-container {
    visibility: visible;
  }

  .results-row {
    text-align:left;
    width: 98%;
    margin: 20px;
    padding: 20px;
    margin-bottom: 0px;
    margin-top:10px;
    padding-top:10px;
  }

  .subsearch-loading {
    height: 20px;
    width: 20px;
    color: grey;
  }

  .results-row.compactOneLine {
    padding: 5px;
  }

  .definiendum-container {
    display: flex;
    flex-direction: column;
    padding-left: 0px;
  }

  .text {
    margin-top: 0px;
    margin-bottom: 5px;
  }

  .tibetan-text {
    font-size: 2.0em;
    margin-bottom: 10px;
  }

  .compactOneLine .tibetan-text {
    margin-bottom: 0;
    font-size: 1.7rem;
  }

  .compact-definition-container {
    margin-top:5px;
    padding: 3px;
  }

  .icon-float {
    float:right;
    margin-right: 10px;
    margin-top: 10px;
    color:rgba(100,100,100,0.1);
    position:relative;
    cursor:pointer;
  }

  .top-right-icon-container {
    position: absolute;
    right: 20px;
    opacity: 0;
    transition: opacity 0.3s ease;
    pointer-events: none;
  }

  .definition-result-container:hover .top-right-icon-container {
    opacity: 1;
    pointer-events: auto;
    z-index: 1000;
  }

  @media (max-width: 768px) {
    .top-right-icon-container {
      opacity: 1;
      pointer-events: auto;
      z-index: 1000;
    }
  }

  .copy-icon, .link-icon, .monlam-icon {
    color:lightgray;
  }

  .copy-icon:hover, .link-icon:hover, .monlam-icon:hover {
    color:rgba(140, 161, 254,1.0);
  }

  .steinert-dict {
    color: gray;
    margin-top: 10px;
  }

  .icon-search {
    font-size: 20px;
    margin-left: 5px;
    padding-top: 5px;
    color: rgb(191, 142, 142);
  }

  .icon-search-steinert {
    font-size: 20px;
    padding-top: 5px;
  }

  .box {
    border-radius: 10px;
    background-color: #f9f9f9;
    padding: 5px 10px;
  }

  .results-container:nth-child(2n) .box {
    border-radius: 10px;
    background-color: #f9f7f1;
    padding: 10px;
  }

  .dict-header-clickable {
    cursor: pointer;
    user-select: none;
    display: inline-flex;
    align-items: center;
    transition: opacity 0.2s;
  }

  .dict-header-clickable:hover {
    opacity: 0.8;
  }

  .chevron-icon {
    margin-right: 8px;
    font-size: 0.8em;
    transition: transform 0.2s;
  }

  /* Collapse transition styles */
  .collapse-enter-active,
  .collapse-leave-active {
    transition: all 0.3s ease;
    overflow: hidden;
  }

  .collapse-enter,
  .collapse-leave-to {
    opacity: 0;
    max-height: 0;
  }

  .collapse-enter-to,
  .collapse-leave {
    opacity: 1;
    max-height: 2000px;
  }

  /* Filtered content notice and button styles */
  .filtered-content-notice {
    padding: 15px;
    margin: 10px 0;
    margin-left: 0;
    padding-left: 0;
    text-align: left;
  }

  .btn-show-filtered,
  .btn-hide-filtered {
    background-color: #ffc107;
    color: #212529;
    border: none;
    padding: 8px 20px;
    border-radius: 4px;
    cursor: pointer;
    font-size: 14px;
    font-weight: 500;
    transition: background-color 0.2s;
  }

  .btn-show-filtered:hover,
  .btn-hide-filtered:hover {
    background-color: #e0a800;
  }

  .btn-hide-filtered {
    background-color: #6c757d;
    color: white;
  }

  .btn-hide-filtered:hover {
    background-color: #5a6268;
  }

  .icn-hide-term {
    transition: all 0.2s;
    margin-top: auto;
    align-self: flex-start;
  }

  .icn-hide-term:hover {
    color: #dc3545;
  }

  /* Alternative matches section */
  .alt-matches-section {
    padding: 0 20px 15px 20px;
    margin-left: 20px;
  }

  .alt-matches-toggle {
    cursor: pointer;
    user-select: none;
    display: inline-flex;
    align-items: center;
    color: #8a7aab;
    font-size: 0.9em;
    font-weight: 500;
    padding: 4px 0;
    transition: opacity 0.2s;
  }

  .alt-matches-toggle:hover {
    opacity: 0.7;
  }

  .alt-matches-label {
    margin-left: 4px;
  }

  .alt-matches-list {
    margin-top: 8px;
    margin-left: 20px;
    border-left: 2px solid #d8d0e4;
    padding-left: 12px;
  }

  .alt-match-item {
    padding: 6px 0;
    border-bottom: 1px solid #f0f0f0;
  }

  .alt-match-item:last-child {
    border-bottom: none;
  }

  .alt-tibetan {
    font-size: 1.4em;
  }

  .alt-variant {
    color: #999;
    font-size: 0.85em;
    margin-left: 4px;
  }

  .alt-english {
    color: #555;
    font-size: 0.9em;
    margin-top: 2px;
  }

  /* Fade-out animation */
  .fade-out {
    animation: fadeOut 0.5s ease-out forwards;
  }

  @keyframes fadeOut {
    0% {
      opacity: 1;
      transform: translateX(0);
    }
    100% {
      opacity: 0;
      transform: translateX(-20px);
    }
  }

  /* Hide Term Confirmation Dialog Styles */
  .hide-term-modal-overlay {
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-color: rgba(0, 0, 0, 0.6);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 10000;
    animation: fadeIn 0.2s ease;
  }

  .hide-term-modal {
    background: white;
    border-radius: 8px;
    box-shadow: 0 10px 40px rgba(0, 0, 0, 0.3);
    max-width: 500px;
    width: 90%;
    animation: slideIn 0.3s ease;
  }

  .hide-term-modal-header {
    padding: 20px 24px;
    border-bottom: 1px solid #dee2e6;
  }

  .hide-term-modal-header h4 {
    margin: 0;
    color: #2c3e50;
    font-size: 1.25rem;
  }

  .hide-term-modal-body {
    padding: 24px;
  }

  .hide-term-modal-body p {
    margin-bottom: 12px;
    color: #495057;
    line-height: 1.6;
  }

  .hide-term-modal-body p:last-child {
    margin-bottom: 0;
  }

  .hide-term-modal-body strong {
    color: #2c3e50;
    font-size: 1.1em;
  }

  .hide-term-modal-footer {
    padding: 16px 24px;
    border-top: 1px solid #dee2e6;
    display: flex;
    justify-content: flex-end;
    gap: 12px;
  }

  .hide-term-modal-footer .btn {
    padding: 8px 16px;
    border-radius: 4px;
    border: none;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.2s ease;
  }

  .hide-term-modal-footer .btn-secondary {
    background-color: #6c757d;
    color: white;
  }

  .hide-term-modal-footer .btn-secondary:hover {
    background-color: #5a6268;
    transform: translateY(-1px);
  }

  .hide-term-modal-footer .btn-primary {
    background-color: #007bff;
    color: white;
  }

  .hide-term-modal-footer .btn-primary:hover {
    background-color: #0056b3;
    transform: translateY(-1px);
  }

  @keyframes fadeIn {
    from {
      opacity: 0;
    }
    to {
      opacity: 1;
    }
  }

  @keyframes slideIn {
    from {
      transform: translateY(-20px);
      opacity: 0;
    }
    to {
      transform: translateY(0);
      opacity: 1;
    }
  }

</style>
